import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2D — Usual Crew widget.
 * "Sai, Aarav, Vikram and 3 others are active this weekend."
 * Drives emotional retention — when your friends are playing, you want in.
 */
export default function ArenaxUsualCrew() {
  const [data, setData] = useState({ crew: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/arenax/player/usual-crew?lookahead_days=7')
      .then(r => setData(r.data || { crew: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  const crew = data.crew || [];
  if (crew.length === 0) return null;

  const names = crew.slice(0, 3).map(c => c.player_name?.split(' ')[0] || 'Friend');
  const more = crew.length > 3 ? ` and ${crew.length - 3} other${crew.length - 3 !== 1 ? 's' : ''}` : '';
  const headline = `${names.join(', ')}${more} ${crew.length === 1 ? 'is' : 'are'} active this week`;

  // Aggregate upcoming games for "Jump into the action"
  const upcomingGameIds = new Set();
  crew.forEach(c => { if (c.next_game) upcomingGameIds.add(c.next_game.id); });
  const firstNextGame = crew[0]?.next_game;

  return (
    <div className="rounded-2xl border border-cyan-500/20 p-5"
      data-testid="arenax-usual-crew"
      style={{ background: 'linear-gradient(160deg, rgba(6,182,212,0.07), rgba(168,85,247,0.04))' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-cyan-300" />
          <span className="text-[10px] tracking-[0.3em] text-cyan-300 font-bold uppercase">YOUR USUAL CREW</span>
        </div>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-emerald-300 border border-emerald-500/30 bg-emerald-500/10">
          {crew.length} active
        </span>
      </div>
      <h3 className="text-lg font-bold text-slate-100 leading-snug mb-3" data-testid="arenax-usual-crew-headline">
        {headline}.
      </h3>
      {firstNextGame && (
        <Link to={`/arenax/games/${firstNextGame.id}`}
          data-testid="arenax-usual-crew-cta"
          className="inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 font-bold">
          Jump into the action <ArrowRight className="h-3 w-3" />
        </Link>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {crew.slice(0, 8).map(c => (
          <span key={c.player_id}
            data-testid={`arenax-crew-chip-${c.player_id}`}
            className="text-[11px] px-2 py-1 rounded-md text-slate-200 bg-slate-900/60 border border-slate-700">
            {c.player_name?.split(' ')[0]} · {c.games_together}× played
          </span>
        ))}
      </div>
    </div>
  );
}
